import { useMemo, useState } from "react";
import {
  Activity,
  CalendarClock,
  CheckCircle2,
  CircleDollarSign,
  ClipboardPenLine,
  FileText,
  Plus,
  RefreshCw,
  Search,
  Sparkles,
  Stethoscope,
  WalletCards,
  X,
} from "lucide-react";
import { agendaDirectory } from "./access/patientDirectory";
import { db, auth } from "./firebase";
import {
  addDoc,
  collection,
  doc,
  serverTimestamp,
  updateDoc,
} from "./access/firestore";
import { registerPayment } from "./services/registerPayment";
import { createAppointment } from "./services/createAppointment";
import EvolutionCard from "./EvolutionCard";

const todayKey = () => {
  const date = new Date();
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 10);
};

const currentTime = () =>
  new Intl.DateTimeFormat("es-MX", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date());

const emptyNote = () => ({
  fecha: todayKey(),
  hora: currentTime(),
  motivo: "",
  tratamiento: "",
  notas: "",
  presupuesto: "",
  pagado: "",
  proximo: "",
  agendarCita: false,
  proximaFecha: "",
  proximaHora: "",
  proximaDuracion: "30",
  proximoMotivo: "",
  proximoPresupuesto: "",
  metodoPago: "efectivo",
  enviarReciboWhatsApp: true,
});

const money = (value) =>
  `$${Number(value || 0).toLocaleString("es-MX", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const sortDate = (item) =>
  new Date(`${item?.fecha || "1970-01-01"}T${item?.hora || "00:00"}`).getTime();

const dateLabel = (value) => {
  if (!value) return "Sin fecha";
  const date = new Date(`${value}T12:00:00`);
  return new Intl.DateTimeFormat("es-MX", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
};

function Field({ label, hint, children, className = "" }) {
  return (
    <label className={`vd-evolution-field ${className}`}>
      <span className="vd-evolution-field__label">{label}</span>
      {hint && <span className="vd-evolution-field__hint">{hint}</span>}
      {children}
    </label>
  );
}

function Metric({ icon: Icon, label, value, tone = "blue" }) {
  return (
    <div className={`vd-evolution-metric vd-evolution-metric--${tone}`}>
      <span className="vd-evolution-metric__icon"><Icon size={18} /></span>
      <span>
        <small>{label}</small>
        <strong>{value}</strong>
      </span>
    </div>
  );
}

export default function EvolutionPanel({ paciente, clinicId, clinicData, citas, pagos }) {
  const [view, setView] = useState("timeline");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [form, setForm] = useState(emptyNote);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [treatment, setTreatment] = useState("Todos");
  const [paymentSearch, setPaymentSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const patientName = `${paciente?.nombre || ""} ${paciente?.apellidos || ""}`.trim();
  const records = useMemo(
    () => (citas || []).filter((item) => item?.pacienteId === paciente?.id || !item?.pacienteId),
    [citas, paciente?.id],
  );
  const sortedRecords = useMemo(
    () => [...records].sort((a, b) => sortDate(b) - sortDate(a)),
    [records],
  );
  const treatments = useMemo(
    () => [
      "Todos",
      ...Array.from(new Set(records.map((item) => item.tratamiento).filter(Boolean))).sort(),
    ],
    [records],
  );
  const filteredRecords = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return sortedRecords.filter((item) => {
      if (dateFrom && String(item.fecha || "") < dateFrom) return false;
      if (dateTo && String(item.fecha || "") > dateTo) return false;
      if (treatment !== "Todos" && item.tratamiento !== treatment) return false;
      if (
        needle &&
        !`${item.motivo || ""} ${item.tratamiento || ""} ${item.notas || ""} ${item.doctorNombre || ""}`
          .toLowerCase()
          .includes(needle)
      ) return false;
      return true;
    });
  }, [dateFrom, dateTo, search, sortedRecords, treatment]);
  const filteredPayments = useMemo(() => {
    const needle = paymentSearch.trim().toLowerCase();
    return [...(pagos || [])]
      .filter((item) => !needle || `${item.folio || ""} ${item.concepto || ""} ${item.metodoPago || ""}`.toLowerCase().includes(needle))
      .sort((a, b) => (b.fecha?.seconds || 0) - (a.fecha?.seconds || 0));
  }, [pagos, paymentSearch]);
  const totalBilled = records.reduce((sum, item) => sum + Number(item.presupuesto || 0), 0);
  const totalPaid = records.reduce((sum, item) => sum + Number(item.pagado || 0), 0);
  const outstanding = records.reduce((sum, item) => sum + Math.max(0, Number(item.saldo ?? Number(item.presupuesto || 0) - Number(item.pagado || 0))), 0);
  const signed = records.filter((item) => item.firmaURL || item.firmadaAt || item.firmadaPor).length;
  const update = (key, value) => setForm((before) => ({ ...before, [key]: value }));
  const clearFilters = () => {
    setSearch("");
    setDateFrom("");
    setDateTo("");
    setTreatment("Todos");
  };
  const closeDrawer = () => {
    if (saving) return;
    setDrawerOpen(false);
    setError("");
  };
  const openNew = () => {
    setForm(emptyNote());
    setError("");
    setDrawerOpen(true);
  };

  const saveNote = async (event) => {
    event.preventDefault();
    const budget = Number(form.presupuesto || 0);
    const paid = Number(form.pagado || 0);
    if (!form.fecha || !form.hora) return setError("Indica la fecha y la hora de la consulta.");
    if (!form.motivo.trim() && !form.tratamiento.trim() && !form.notas.trim()) return setError("Agrega al menos el motivo, el tratamiento o la nota clínica.");
    if (budget < 0 || paid < 0 || paid > budget) return setError("Revisa el presupuesto y el pago recibido. El pago no puede superar el presupuesto.");
    if (form.agendarCita && (!form.proximaFecha || !form.proximaHora || !form.proximoMotivo.trim())) return setError("Completa fecha, hora y motivo de la siguiente cita.");
    setSaving(true);
    setError("");
    try {
      const uid = auth.currentUser?.uid || "";
      const doctorName = auth.currentUser?.displayName || auth.currentUser?.email || "";
      const balance = Math.max(0, budget - paid);
      const record = {
        pacienteId: paciente.id,
        pacienteNombre: patientName,
        fecha: form.fecha,
        hora: form.hora,
        motivo: form.motivo.trim(),
        tratamiento: form.tratamiento.trim(),
        notas: form.notas.trim(),
        proximo: form.proximo.trim(),
        presupuesto: budget,
        pagado: 0,
        saldo: budget,
        duracion: "30",
        estado: balance === 0 ? "pagada" : "activa",
        doctorId: uid,
        doctorNombre: doctorName,
        doctorColor: "#1686ad",
        origen: "manual",
        tipoRegistro: "evolucion",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        updatedBy: uid,
      };
      const created = await addDoc(agendaDirectory(db, clinicId), record);
      const createdRecord = { id: created.id, ...record, cuentaPorCobrarId: null };
      if (budget > 0) {
        const account = await addDoc(
          collection(db, "clinics", clinicId, "pacientes", paciente.id, "cuentasPorCobrar"),
          {
            clinicId,
            tipo: "evolucion",
            citaId: created.id,
            descripcion: form.motivo.trim() || "Consulta dental",
            total: budget,
            pagado: 0,
            saldo: budget,
            estado: "Pendiente",
            createdAt: serverTimestamp(),
          },
        );
        createdRecord.cuentaPorCobrarId = account.id;
        await updateDoc(doc(db, "clinics", clinicId, "citas", created.id), { cuentaPorCobrarId: account.id });
      }
      if (paid > 0) {
        await registerPayment({
          clinicId,
          clinicData,
          paciente,
          cita: createdRecord,
          monto: paid,
          metodoPago: form.metodoPago,
          enviarReciboWhatsApp: form.enviarReciboWhatsApp,
        });
      }
      if (form.agendarCita) {
        await createAppointment({
          clinicId,
          clinicData,
          paciente,
          form: {
            fecha: form.proximaFecha,
            hora: form.proximaHora,
            motivo: form.proximoMotivo.trim(),
            tratamiento: "",
            presupuesto: form.proximoPresupuesto,
            duracion: form.proximaDuracion,
            notas: "",
            proximo: "",
            estado: "activa",
            doctorId: uid,
            doctorNombre: doctorName,
            doctorColor: "#1686ad",
          },
        });
      }
      setDrawerOpen(false);
      setForm(emptyNote());
    } catch (caught) {
      setError(caught?.message || "No pudimos guardar la evolución. Intenta nuevamente.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="vd-evolution-page" aria-label="Evolución del paciente">
      <header className="vd-evolution-hero">
        <div className="vd-evolution-hero__copy">
          <span className="vd-evolution-eyebrow"><Activity size={15} /> Expediente clínico</span>
          <h2>Evolución clínica</h2>
          <p>Registra cada consulta, tratamiento y seguimiento en una línea clínica clara.</p>
          <div className="vd-evolution-live"><span /> Sincronizado automáticamente con el expediente</div>
        </div>
        <div className="vd-evolution-hero__patient">
          <span className="vd-evolution-avatar">{patientName.split(" ").map((part) => part[0]).slice(0, 2).join("") || "P"}</span>
          <div><small>Paciente</small><strong>{patientName || "Paciente"}</strong></div>
          <button type="button" className="vd-evolution-primary" onClick={openNew}><Plus size={18} /> Nueva evolución</button>
        </div>
      </header>

      <div className="vd-evolution-metrics">
        <Metric icon={FileText} label="Registros clínicos" value={records.length} tone="blue" />
        <Metric icon={CheckCircle2} label="Notas firmadas" value={`${signed}/${records.length || 0}`} tone="teal" />
        <Metric icon={CircleDollarSign} label="Saldo pendiente" value={money(outstanding)} tone="amber" />
        <Metric icon={WalletCards} label="Pagado en consultas" value={money(totalPaid)} tone="ink" />
      </div>

      <div className="vd-evolution-switcher" role="tablist" aria-label="Información de evolución">
        <button type="button" role="tab" aria-selected={view === "timeline"} className={view === "timeline" ? "is-active" : ""} onClick={() => setView("timeline")}><Activity size={17} /> Línea clínica <span>{records.length}</span></button>
        <button type="button" role="tab" aria-selected={view === "payments"} className={view === "payments" ? "is-active" : ""} onClick={() => setView("payments")}><CircleDollarSign size={17} /> Pagos <span>{pagos?.length || 0}</span></button>
      </div>

      {view === "timeline" ? (
        <>
          <div className="vd-evolution-toolbar">
            <div className="vd-evolution-search"><Search size={17} /><input aria-label="Buscar en evolución" placeholder="Buscar motivo, tratamiento o nota..." value={search} onChange={(event) => setSearch(event.target.value)} /></div>
            <div className="vd-evolution-filter-row">
              <label><span>Desde</span><input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} /></label>
              <label><span>Hasta</span><input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} /></label>
              <label><span>Tratamiento</span><select value={treatment} onChange={(event) => setTreatment(event.target.value)}>{treatments.map((item) => <option key={item}>{item}</option>)}</select></label>
              {(search || dateFrom || dateTo || treatment !== "Todos") && <button type="button" className="vd-evolution-quiet-button" onClick={clearFilters}><RefreshCw size={15} /> Limpiar</button>}
            </div>
          </div>
          <div className="vd-evolution-list-head"><div><span className="vd-evolution-section-kicker"><Stethoscope size={15} /> Línea de tiempo</span><h3>Consultas y seguimiento</h3></div><span className="vd-evolution-result-count">{filteredRecords.length} de {records.length}</span></div>
          {filteredRecords.length ? (
            <div className="vd-evolution-timeline">
              {filteredRecords.map((item) => <EvolutionCard key={item.id} cita={item} clinicId={clinicId} formatearFecha={dateLabel} />)}
            </div>
          ) : (
            <div className="vd-evolution-empty"><span><ClipboardPenLine size={27} /></span><h3>{records.length ? "No encontramos coincidencias" : "Aún no hay evoluciones"}</h3><p>{records.length ? "Prueba con otro texto o limpia los filtros." : "Registra la primera consulta para comenzar el seguimiento clínico."}</p>{records.length ? <button type="button" onClick={clearFilters}>Limpiar filtros</button> : <button type="button" onClick={openNew}><Plus size={16} /> Registrar evolución</button>}</div>
          )}
        </>
      ) : (
        <>
          <div className="vd-evolution-finance-hero"><div><span className="vd-evolution-section-kicker"><WalletCards size={15} /> Control financiero</span><h3>Pagos relacionados con este paciente</h3><p>La información se actualiza automáticamente cuando se registra un pago.</p></div><strong>{money(totalBilled)}</strong></div>
          <div className="vd-evolution-search vd-evolution-search--payments"><Search size={17} /><input aria-label="Buscar pagos" placeholder="Buscar folio, concepto o método..." value={paymentSearch} onChange={(event) => setPaymentSearch(event.target.value)} /></div>
          <div className="vd-evolution-payment-list">{filteredPayments.length ? filteredPayments.map((payment) => <article className="vd-evolution-payment-card" key={payment.id}><span className="vd-evolution-payment-icon"><CircleDollarSign size={18} /></span><div><strong>{payment.concepto || "Pago registrado"}</strong><small>{payment.fecha?.seconds ? new Date(payment.fecha.seconds * 1000).toLocaleString("es-MX") : "Fecha no disponible"} · {payment.metodoPago || "Método no indicado"}</small><span>Folio {payment.folio || "sin folio"}</span></div><b>{money(payment.monto)}</b></article>) : <div className="vd-evolution-empty"><span><CircleDollarSign size={27} /></span><h3>No hay pagos para mostrar</h3><p>Los pagos registrados aparecerán aquí.</p></div>}</div>
        </>
      )}

      {drawerOpen && (
        <div className="vd-evolution-drawer-layer" role="presentation" onMouseDown={closeDrawer}>
          <form className="vd-evolution-drawer" onSubmit={saveNote} onMouseDown={(event) => event.stopPropagation()}>
            <header className="vd-evolution-drawer__header"><div><span className="vd-evolution-eyebrow"><Sparkles size={15} /> Nuevo registro</span><h2>Registrar evolución</h2><p>{patientName || "Paciente"} · quedará sincronizada con su expediente</p></div><button type="button" className="vd-evolution-icon-button" aria-label="Cerrar formulario" onClick={closeDrawer}><X size={20} /></button></header>
            <div className="vd-evolution-drawer__body">
              {error && <div className="vd-evolution-form-error" role="alert">{error}</div>}
              <section className="vd-evolution-form-section"><div className="vd-evolution-form-section__title"><span>1</span><div><strong>Consulta</strong><small>Lo esencial de la atención de hoy</small></div></div><div className="vd-evolution-form-grid vd-evolution-form-grid--date"><Field label="Fecha"><input required type="date" value={form.fecha} onChange={(event) => update("fecha", event.target.value)} /></Field><Field label="Hora"><input required type="time" value={form.hora} onChange={(event) => update("hora", event.target.value)} /></Field></div><Field label="Motivo de consulta" hint="Qué trae al paciente hoy"><input autoFocus placeholder="Ej. Dolor en molar inferior derecho" value={form.motivo} onChange={(event) => update("motivo", event.target.value)} /></Field><Field label="Tratamiento realizado"><input placeholder="Ej. Restauración provisional" value={form.tratamiento} onChange={(event) => update("tratamiento", event.target.value)} /></Field><Field label="Nota clínica" hint="Hallazgos, respuesta y recomendaciones"><textarea rows={5} placeholder="Escribe la evolución de forma clara..." value={form.notas} onChange={(event) => update("notas", event.target.value)} /></Field></section>
              <section className="vd-evolution-form-section"><div className="vd-evolution-form-section__title"><span>2</span><div><strong>Cobro de la consulta <em>Opcional</em></strong><small>Registra presupuesto y pago sin salir del expediente</small></div></div><div className="vd-evolution-form-grid"><Field label="Presupuesto"><div className="vd-evolution-money-input"><span>$</span><input type="number" min="0" step="0.01" placeholder="0.00" value={form.presupuesto} onChange={(event) => update("presupuesto", event.target.value)} /></div></Field><Field label="Pago recibido"><div className="vd-evolution-money-input"><span>$</span><input type="number" min="0" step="0.01" placeholder="0.00" value={form.pagado} onChange={(event) => update("pagado", event.target.value)} /></div></Field></div><div className="vd-evolution-balance-row"><span>Saldo pendiente</span><strong>{money(Math.max(0, Number(form.presupuesto || 0) - Number(form.pagado || 0)))}</strong></div>{Number(form.pagado || 0) > 0 && <div className="vd-evolution-inline-options"><Field label="Método"><select value={form.metodoPago} onChange={(event) => update("metodoPago", event.target.value)}><option value="efectivo">Efectivo</option><option value="tarjeta">Tarjeta</option><option value="transferencia">Transferencia</option></select></Field><label className="vd-evolution-check"><input type="checkbox" checked={form.enviarReciboWhatsApp} onChange={(event) => update("enviarReciboWhatsApp", event.target.checked)} /><span>Enviar recibo por WhatsApp</span></label></div>}</section>
              <section className="vd-evolution-form-section"><div className="vd-evolution-form-section__title"><span>3</span><div><strong>Seguimiento</strong><small>Deja lista la siguiente acción</small></div></div><Field label="Próximo tratamiento" hint="Opcional"><input placeholder="Ej. Control de cicatrización" value={form.proximo} onChange={(event) => update("proximo", event.target.value)} /></Field><label className="vd-evolution-next-toggle"><span><CalendarClock size={18} /><span><strong>Agendar siguiente cita</strong><small>Se creará automáticamente al guardar</small></span></span><input type="checkbox" checked={form.agendarCita} onChange={(event) => update("agendarCita", event.target.checked)} /></label>{form.agendarCita && <div className="vd-evolution-next-fields"><div className="vd-evolution-form-grid vd-evolution-form-grid--date"><Field label="Fecha"><input required type="date" value={form.proximaFecha} onChange={(event) => update("proximaFecha", event.target.value)} /></Field><Field label="Hora"><input required type="time" value={form.proximaHora} onChange={(event) => update("proximaHora", event.target.value)} /></Field></div><div className="vd-evolution-form-grid"><Field label="Motivo"><input required placeholder="Ej. Revisión" value={form.proximoMotivo} onChange={(event) => update("proximoMotivo", event.target.value)} /></Field><Field label="Duración"><select value={form.proximaDuracion} onChange={(event) => update("proximaDuracion", event.target.value)}><option value="15">15 minutos</option><option value="30">30 minutos</option><option value="45">45 minutos</option><option value="60">60 minutos</option><option value="90">90 minutos</option></select></Field></div><Field label="Presupuesto estimado"><div className="vd-evolution-money-input"><span>$</span><input type="number" min="0" step="0.01" placeholder="0.00" value={form.proximoPresupuesto} onChange={(event) => update("proximoPresupuesto", event.target.value)} /></div></Field></div>}</section>
            </div>
            <footer className="vd-evolution-drawer__footer"><button type="button" className="vd-evolution-secondary" onClick={closeDrawer}>Cancelar</button><button type="submit" className="vd-evolution-primary" disabled={saving}>{saving ? <><RefreshCw size={16} className="vd-evolution-spin" /> Guardando...</> : <><CheckCircle2 size={17} /> Guardar evolución</>}</button></footer>
          </form>
        </div>
      )}
    </section>
  );
}
